import type { TrpcOutput } from "../../utils/trpc.js";

export type ChatMessage = {
  id: string;
  kind: string;
  text: string;
  created: string;
  relatedSources?: RelatedSource[];
};

export type ConversationRow = NonNullable<TrpcOutput["aier"]["getConversations"]>["results"][number];

// Which AIER endpoint a conversation talks to — drives which chat mutation continues it.
export type ChatMode = "chat" | "chat_article";

export type RelatedSource = TrpcOutput["aier"]["sendChatMessage"]["relatedSources"][number];

// Maps a conversation's AIER `source` to the mode used to continue it
export const SOURCE_TO_MODE: Record<ConversationRow["source"], ChatMode> = {
  program: "chat",
  article: "chat_article",
};

// Button label + intro message shown for each mode when starting a new conversation.
export const CHAT_MODE_INFO: Record<ChatMode, { label: string; description: string }> = {
  chat: {
    label: "Ask a question",
    description:
      "Ask me anything about the content. From clarifying concepts and explaining terminology to applying ideas to your own role. There are no wrong questions, and I'll tailor my responses to this programme.",
  },
  chat_article: {
    label: "Explore IbyIMD",
    description:
      "Explore articles, research, and thought leadership from IMD. Ask questions about what you're reading, discover related ideas, or connect insights back to your own work and leadership practice.",
  },
};

// AIER sometimes emits citation markers as `<sup>^1</sup>` — the caret is a leftover
// from its plain-text `^1^` convention and isn't markdown syntax, so it renders literally.
export function stripRedundantSupCaret(text: string): string {
  return text.replace(/(<sup>)\^/gi, "$1");
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
